import { Component } from 'react';
import * as PropTypes from 'prop-types';
import { cloneDeep, get } from 'lodash';

import BEM from 'lib/bem';
import config from 'config';

import { hasSubtitlesChanged } from 'models/asset';
import { createPlayer } from './newPlayerApi';
import { PLAYER_STATE } from './playerApi';
import { useSignAssetStreamUrls } from './hooks';
import './NewPlayer.scss';
import { adminBffClient } from '../../../services';

const bem = new BEM('new-player');

const PLAYER_PROP_TYPES = {
    asset: PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
        provider: PropTypes.string.isRequired,
        duration: PropTypes.number.isRequired,
        streamType: PropTypes.string.isRequired,
        assetType: PropTypes.string.isRequired,
        images: PropTypes.shape({
            main: PropTypes.string.isRequired,
            snapshots: PropTypes.string,
        }).isRequired,
        streamUrls: PropTypes.shape({
            hls: PropTypes.string.isRequired,
        }).isRequired,
        flightTimes: PropTypes.shape({
            start: PropTypes.number,
            end: PropTypes.number,
        }),
        additional: PropTypes.object.isRequired,
        streamConfiguration: PropTypes.shape({
            properties: PropTypes.arrayOf(PropTypes.string).isRequired,
        }).isRequired,
    }).isRequired,
    'data-testid': PropTypes.string,
    createStreamPlayer: PropTypes.func.isRequired,
    play: PropTypes.bool,
    mute: PropTypes.bool,
    autoplay: PropTypes.bool,
    position: PropTypes.number,
    options: PropTypes.object,
    onChange: PropTypes.func,
    onReady: PropTypes.func,
    pauseOnSeek: PropTypes.bool,
    expandHeight: PropTypes.bool,
    className: PropTypes.string,
    noPoster: PropTypes.bool,
    onSeek: PropTypes.func,
};

class NewPlayer extends Component {
    player = {
        isLoaded: false,
    };

    static propTypes = PLAYER_PROP_TYPES;

    static defaultProps = {
        'data-testid': undefined,
        play: false,
        mute: false,
        autoplay: false,
        position: null,
        pauseOnSeek: false,
        options: {},
        onChange: () => {},
        onReady: () => {},
        expandHeight: false,
        noPoster: false,
        className: undefined,
    };

    state = {
        play: this.props.play,
    };

    constructor(props) {
        super(props);
        this.nodeId = `player-${new Date().getTime()}`;
    }

    componentDidMount() {
        this.initializePlayer(this.props);
    }

    componentDidUpdate({ asset: oldAsset, ...prevProps }) {
        const { player } = this;
        const { asset, position } = this.props;
        const { play } = this.state;

        if (!player || player.isLoaded === false) {
            return;
        }

        if (asset.streamType === 'live' && position === -1) {
            player.seekLive(play);
        } else if (prevProps.position !== position) {
            player.seek((asset.duration / 1000) * position, play);
        }

        if (
            this.hasAssetChanged(oldAsset) ||
            this.hasAssetSourceFilesChanged(oldAsset) ||
            this.hasAssetSubtitlesChanged(oldAsset) ||
            this.hasAssetPosterChanged(oldAsset)
        ) {
            player.load(cloneDeep(asset));
        }
    }

    componentWillUnmount() {
        const { player } = this;

        if (player && player.remove && player.isLoaded) {
            player.remove();
        }
    }

    initializePlayer = async ({
        asset,
        createStreamPlayer,
        onReady,
        onChange,
        autoplay,
        mute,
        position,
        options,
        pauseOnSeek,
        onSeek,
    }) => {
        const startTime = asset.duration && position ? Math.round((asset.duration / 1000) * position) : null;
        const isAudioAsset = asset.assetType === 'audio';

        const drmToken = async ({ id, vendor }) => {
            try {
                const { token } = await adminBffClient.generateDrmToken({ provider: vendor, assetId: id });

                return token;
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error(err);
            }

            return '';
        };

        const skin = isAudioAsset
            ? {
                  dnaStyle: 'vg',
                  coverUrl: asset.images.main,
                  title: asset.title,
                  ...(asset.description && { description: asset.description }),
                  ...(asset.published && { publishedDate: new Date(asset.published * 1000) }),
              }
            : { dnaStyle: 'vg' };

        const streamPlayerInstance = createStreamPlayer({
            vendor: asset.provider,
            asset: cloneDeep(asset),
            node: this.nodeId,
            api: config.player.options.api,
            tokenUrl: config.player.options.tokenUrl,
            thumbnailsUrl: config.player.options.thumbnailsUrl,
            autoplay: autoplay || config.player.options.autoplay,
            muted: mute,
            loop: options?.repeat || false,
            skipCountdown: options?.skipCountdown || false,
            // Legacy parity with `config.player.options.settings.preview: true`:
            // assets in admin are unpublished by definition, so the pre-playback
            // playability probe must not reject them with 14001 (NOT_ACTIVE) —
            // which aborts the load before the countdown is even armed.
            skipEntitlementCheck: true,
            time: startTime,
            audio: isAudioAsset,
            skin,
            drmToken,
        });

        const player = createPlayer(streamPlayerInstance);
        player.onStateChange = (newState) => onChange(newState);

        const { streamPlayer } = player;

        streamPlayer.on('error', () => {
            this.player = null;
        });
        streamPlayer.on('ready', () => onReady(player));
        streamPlayer.on('timeupdate', () => {
            const { play } = this.state;
            if (play === false) {
                player.pause();
            }
        });

        if (pauseOnSeek) {
            streamPlayer.on('seeked', (currentTime) => {
                if (onSeek) {
                    onSeek(currentTime);
                }
                player.pause();
            });
        }

        streamPlayer.on('play', () => {
            this.setState({ play: true });
        });

        this.player = player;
    };

    hasAssetSourceFilesChanged(asset) {
        return get(this.props.asset, 'additional.sourceFiles.length') !== get(asset, 'additional.sourceFiles.length');
    }

    hasAssetSubtitlesChanged(asset) {
        return hasSubtitlesChanged(asset, this.props.asset.additional.subtitles);
    }

    hasAssetChanged(oldAsset) {
        const hasPropertyChanged = (propertyName) =>
            get(this.props.asset, propertyName) !== get(oldAsset, propertyName);

        const result = [
            'additional.metadata.source',
            'additional.metadata.drmPlaybackUrl',
            'streamUrls.hls',
            'duration',
            'playback.begin',
            'playback.end',
        ].some(hasPropertyChanged);

        return this.props.asset.streamType !== 'live'
            ? result
            : result || ['flightTimes.start', 'flightTimes.end'].some(hasPropertyChanged);
    }

    hasAssetPosterChanged(oldAsset) {
        return !this.state.play && this.props.asset?.images.main !== oldAsset?.images.main;
    }

    render() {
        const { className } = this.props;

        return (
            <div className={[bem.block(undefined), className].join(' ')} data-testid={this.props['data-testid']}>
                <div
                    id={this.nodeId}
                    className={bem.element('player', {
                        audio: this.props.asset.assetType === 'audio',
                    })}
                />
            </div>
        );
    }
}

export function SignStreamUrls({ asset, ...rest }) {
    const { asset: signedAsset, isLoading } = useSignAssetStreamUrls(asset);
    if (isLoading) {
        return null;
    }
    return <NewPlayer {...rest} asset={signedAsset} />;
}

SignStreamUrls.propTypes = PLAYER_PROP_TYPES;

export { SignStreamUrls as default, PLAYER_STATE };
